import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSingleEffect } from 'react-haiku';
import { useCollect } from 'hooks';
import { orderBy, uniqBy } from 'lodash';

import Progress from 'components/Misc/Progress';

import Add from 'containers/Administrator/Users/Profile/Student/Markz/Add';
import Guide from 'containers/Administrator/Users/Profile/Student/Markz/Guide';
import Subject from 'containers/Administrator/Users/Profile/Student/Markz/Subject';
import GradeTabs from 'containers/Administrator/Users/Profile/Student/Markz/Tabs/Grades';

import Finals from 'containers/Administrator/Users/Profile/Student/Markz/Finals';
import Awards from 'containers/Administrator/Users/Profile/Student/Markz/Awards';

import { useStudentMarkStore } from 'store/user/useStudentMarkStore';

// Store request parameters
const parameters = {
	eager: '[grades.program, courses.subject, takes.exam, marks.subject]'
};

const Marks = ({ tab, setTab }) => {
	// Cache current object's id
	const { id } = useParams();

	// Update selected tab on mount
	useSingleEffect(() => setTab(tab));

	// Use student's mark store
	const [grade, courses, marks, active, onInit] = useStudentMarkStore(state => [
		state.grades.find(g => g.id === state.active),
		state.courses,
		state.marks,
		state.active,
		state.onInit
	]);

	// Store requested user
	const [[, progress], collect] = useCollect(`users/${id}`, parameters, onInit);

	// Filter out subjects that are in current grades
	const subjects = useMemo(() => {
		// Filter subject's from courses
		const fromCourses = courses?.reduce((result, course) => {
			return result.some(subject => subject.id === course.subjectId) || grade?.id !== course.gradeId
				? result
				: [...result, course.subject];
		}, []);

		// Filter subject's from marks
		const fromMarks = marks?.reduce((result, mark) => {
			return result.some(subject => subject.id === mark.subjectId) || grade?.id !== mark.gradeId
				? result
				: [...result, mark.subject];
		}, []);

		// Remove duplicates and sort results by subject's title
		return orderBy(uniqBy([...fromCourses, ...fromMarks], 'id'), 'order');
	}, [grade, courses, marks, active]);

	// Collect user on mount and on parameter change
	useSingleEffect(() => {
		(async () => await collect())();
	});

	return (
		<div className="mt-4 p-4 space-y-4 bg-white dark:bg-neutral-800 rounded-lg transition-colors">
			{/* Show loading progress */}
			<Progress progress={!subjects?.length && progress} />

			{/* Grade tabs */}
			<GradeTabs />

			{/* Display student's marks */}
			<div className="max-w-full">
				<table className="w-full border-separate border-spacing-0">
					<thead>
						<tr>
							<th className="w-56 p-2 pl-3 first:border-l first:rounded-tl-lg last:rounded-tr-lg border-r border-b border-t border-neutral-100 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 whitespace-nowrap">Mācību kurss</th>
							<th className="p-2 first:border-l first:rounded-tl-lg last:rounded-tr-lg border-r border-b border-t border-neutral-100 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 whitespace-nowrap">Moduļi</th>
							<th className="w-11 p-2 first:border-l first:rounded-tl-lg last:rounded-tr-lg border-r border-b border-t border-neutral-100 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 whitespace-nowrap">G</th>
						</tr>
					</thead>
					<tbody>
						{subjects?.map(subject =>
							<Subject key={subject.id} subject={subject} />
						)}
					</tbody>
				</table>
			</div>

			{/* Style guide map */}
			<Guide />

			{/* Finals test */}
			<Finals />
			
			{/* Awards test */}
			<Awards />

			{/* Mark addition modal */}
			<Add />
		</div>
	);
};

export default Marks;